import { service } from '@ember/service'
import Component from '@glimmer/component'
import RouterService from '@ember/routing/router-service'
import { registerDestructor } from '@ember/destroyable'

export default class BaseRouteTemplate<T extends PageConfig> extends Component {
  @service router: RouterService
  @service flowManager: FlowManager
  @service frameManager: FrameManager

  constructor(owner: unknown, args: any) {
    super(owner, args)
    this._setupEventHandlers()
    this.frameManager.setEmbedHeight()
  }

  get pageSettings(): Prettify<Omit<T, keyof BasePage>> {
    return this.flowManager.activeFlow?.activePage?.settings as Omit<T, keyof BasePage>
  }

  private _setupEventHandlers() {
    const routeScope = this.router.currentRouteName ?? undefined

    const unsubscribeUpdated = this.flowManager.subscribe('onFlowUpdated', this.onFlowUpdated.bind(this), {
      routeScope
    })
    const unsubscribeExit = this.flowManager.subscribe('onExit', this.onExit.bind(this), {
      routeScope
    })
    const unsubscribeError = this.flowManager.subscribe('onErrorCaptured', this.onErrorCaptured.bind(this), {
      routeScope
    })

    registerDestructor(this, () => {
      unsubscribeUpdated()
      unsubscribeExit()
      unsubscribeError()
    })
  }

  /*********************************************************
                  FLOW EVENT HANDLERS
  *********************************************************/
  // Event handlers are only called for the template that is associated with the current route.
  //
  // Unlike lifecycle hooks, event handlers do not block flow transitions. Additionally,
  // the flow does not wait for asynchronous operations within event handlers to complete
  // before continuing to the next step.
  //
  // Events are emitted after all lifecycle hooks (`onStepMounted`, `onFlowUpdated`, `canAdvanceStep`,
  // `canAdvancePage`) have been executed for the relevant steps.

  /**
   * Called when the flow transitions between steps within the same route.
   * This event is triggered after all lifecycle hooks (`onStepMounted`, `onFlowUpdated`,
   * `canAdvanceStep`) for the transition have been executed.
   *
   * @param {Array<Object>} activeSteps - The currently active steps in the flow.
   * @param {boolean} allStepsValid - Whether all active steps in the route can exit.
   *
   * Usage:
   * - Synchronize external systems (e.g., analytics, logging) with the current flow state.
   * - Update route-level UI or state based on validation status.
   */
  protected onFlowUpdated(_activeSteps: FlowStep[], _allStepsValid: boolean) {}

  /**
   * Called before the flow transitions from the current route to a new route.
   * This event is triggered after all `canAdvancePage` hooks for the current route's
   * steps have been executed.
   *
   * Usage:
   * - Clean up route-specific resources or state (e.g., timers, listeners).
   * - Persist intermediate flow data before navigating to another route.
   */
  protected onExit() {}

  /** Called when an error is thrown in any lifecycle hook (`onStepMounted`, `onFlowUpdated`,
   * `canAdvanceStep`, `canAdvancePage`)
   *
   * @param {Error} error - The error object
   *
   * Usage:
   * - Log or report errors to external systems for monitoring.
   * - Display error messages or trigger recovery logic at the route level.
   */
  protected onErrorCaptured(_error: Error) {}
}
